import logoFullUrl from '@/assets/brand/logo-full.svg';
import logoMarkUrl from '@/assets/brand/logo-mark.svg';

/**
 * Единственный источник бренд-графики в интерфейсе — оба файла в
 * `web/src/assets/brand/` являются точными копиями утверждённых исходников,
 * без перерисовки. Название бренда не переводится (в обеих локалях
 * `common:appName` — одна и та же строка «EsportCRM»), поэтому alt-текст
 * захардкожен, а не идёт через i18n.
 *
 * `LogoMark` — только знак, для тесных мест (шапка на мобильном, сайдбар,
 * компактные заголовки). `LogoFull` — знак с текстом, для мест, где бренд
 * должен быть явно и однозначно виден (экраны входа, публичная витрина).
 *
 * `LogoFullOnDark` — то же самое, но на собственной тёмной подложке.
 * У исходника часть текста («Esport») залита почти белым (`#f7f9ff`) — на
 * тёмном фоне так и задумано, но приложение по умолчанию светлое
 * (`bg-bg` в светлой теме ≈ `rgb(250,250,250)`), и на нём эта часть
 * надписи становится практически невидимой — заметен только градиентный
 * хвост «CRM». Перекрашивать сам SVG нельзя (source of truth без права на
 * перерисовку), поэтому там, где полный логотип стоит на светлом/переменном
 * фоне (шапка входа, публичная витрина), он получает собственную тёмную
 * подложку фиксированного цвета — тот же тон, что и реальный `bg-bg` тёмной
 * темы приложения (`#0b0b0f`), чтобы не выглядеть посторонним пятном.
 */

interface LogoProps {
  className?: string;
}

interface LogoMarkProps extends LogoProps {
  /**
   * Рядом уже есть видимый текст с названием бренда (почти все нынешние
   * места использования `LogoMark` — шапка, сайдбар, заголовки экранов) —
   * тогда знак декоративный: пустой `alt` и `aria-hidden`, чтобы читалка
   * экрана не произносила «EsportCRM» дважды подряд. Без видимого текста
   * рядом (например, будущее отдельное место) оставить `decorative`
   * не заданным — тогда знак остаётся содержательным изображением со своим
   * `alt`.
   */
  decorative?: boolean;
}

export function LogoMark({ className, decorative = false }: LogoMarkProps) {
  return (
    <img
      src={logoMarkUrl}
      alt={decorative ? '' : 'EsportCRM'}
      aria-hidden={decorative || undefined}
      className={className}
    />
  );
}

export function LogoFull({ className }: LogoProps) {
  return <img src={logoFullUrl} alt="EsportCRM" className={className} />;
}

export function LogoFullOnDark({ className }: LogoProps) {
  return (
    <span className="inline-flex items-center rounded-lg bg-[#0b0b0f] px-3 py-1.5">
      <LogoFull className={className} />
    </span>
  );
}
